import { useAuth } from "@/hooks/use-auth";
import { trackEvent } from "@/lib/posthog";
import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetMyMembership, useListMembershipPlans, useMembershipCheckout, useCancelMembership } from "@workspace/api-client-react";
import { Crown, Check, Percent, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { format } from "date-fns";

export default function Memberships() {
  const { user } = useAuth();
  const gymId = user?.gymId || "";
  const { data: membership, refetch: refetchMembership } = useGetMyMembership();
  const { data: plans, isLoading } = useListMembershipPlans(gymId, { query: { enabled: !!gymId } });
  const checkoutMutation = useMembershipCheckout();
  const cancelMutation = useCancelMembership();
  const { toast } = useToast();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleSubscribe = async (planId: string) => {
    try {
      const result = await checkoutMutation.mutateAsync({ data: { planId } });
      trackEvent("membership_checkout_started", { planId, gymId });
      if (result?.url && (result.url.startsWith("http") || result.url.startsWith("//"))) {
        window.location.href = result.url;
      } else {
        toast({ title: "Membership Activated!", description: "Welcome to the family." });
        refetchMembership();
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleCancel = async () => {
    if (!membership?.id) return;
    try {
      await cancelMutation.mutateAsync({ id: membership.id });
      toast({ title: "Membership Cancelled", description: "Your membership will remain active until the effective end date." });
      setShowCancelConfirm(false);
      refetchMembership();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const hasActive = membership && membership.status === "ACTIVE" && membership.id;
  const mem = membership as any;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Memberships</h1>
          <p className="text-muted-foreground text-lg">{hasActive ? "Manage your membership" : "Choose a plan that fits your goals"}</p>
        </div>

        {hasActive && (
          <Card className="glass-panel border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center"><Crown className="w-7 h-7 text-primary" /></div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white">{mem.planName}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {mem.cancelAtPeriodEnd ? "Cancelling" : "Active membership"}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Private Credits</p>
                      <p className="text-2xl font-mono font-bold text-white">{membership.privateCreditsRemaining}</p>
                    </div>
                    {mem.discountPct > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Session Discount</p>
                        <p className="text-2xl font-mono font-bold text-emerald-400">{mem.discountPct}% off</p>
                      </div>
                    )}
                  </div>

                  {mem.cancelAtPeriodEnd && mem.effectiveEndDate && (
                    <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <p className="text-sm text-amber-400 font-medium">
                        Your membership will end on {format(new Date(mem.effectiveEndDate), "MMMM d, yyyy")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        You can continue using your benefits until then.
                      </p>
                    </div>
                  )}

                  {!mem.cancelAtPeriodEnd && (
                    <div className="mt-4">
                      <Button variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-sm" onClick={() => setShowCancelConfirm(true)}>
                        Cancel Membership
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {showCancelConfirm && hasActive && (
          <Card className="glass-panel border-red-500/30 bg-red-500/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">Confirm Cancellation</h3>
                  <p className="text-sm text-muted-foreground mb-1">
                    Your membership requires a 30-day cancellation notice.
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    After cancelling, your membership will remain active for at least 30 more days (or until the end of your current billing period, whichever is later). You'll continue to have access to all your benefits during this time.
                  </p>
                  <div className="flex gap-3">
                    <Button onClick={handleCancel} disabled={cancelMutation.isPending} className="bg-red-500 hover:bg-red-600 text-white font-bold">
                      {cancelMutation.isPending ? "Cancelling..." : "Yes, Cancel Membership"}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowCancelConfirm(false)} className="text-muted-foreground">
                      Keep Membership
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[1, 2, 3].map(i => <div key={i} className="h-72 bg-white/5 rounded-2xl animate-pulse" />)}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(plans || []).map((plan, i) => (
              <Card key={plan.id} className={`glass-panel border overflow-hidden hover:-translate-y-1 transition-all ${i === 1 ? "border-primary shadow-lg shadow-primary/10" : "border-white/5"}`}>
                {i === 1 && <div className="bg-primary text-black text-center text-xs font-bold py-1.5 uppercase tracking-wider">Recommended</div>}
                <CardContent className="p-6 flex flex-col">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  {plan.description && <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>}
                  <div className="mb-6">
                    {plan.discountPct > 0 ? (
                      <>
                        <span className="text-xl font-mono text-white/40 line-through">${plan.priceMonthly}</span>{" "}
                        <span className="text-4xl font-mono font-bold text-white">${(plan.priceMonthly * (1 - plan.discountPct / 100)).toFixed(2)}</span>
                        <span className="text-muted-foreground">/month</span>
                        <div className="mt-1">
                          <span className="text-xs text-emerald-400 font-medium">{plan.discountPct}% member discount</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-mono font-bold text-white">${plan.priceMonthly}</span>
                        <span className="text-muted-foreground">/month</span>
                      </>
                    )}
                  </div>
                  <div className="space-y-3 mb-6 flex-1">
                    {plan.privateCredits > 0 && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span className="text-white text-sm">{plan.privateCredits} private sessions</span>
                      </div>
                    )}
                    {plan.privateCredits > 0 && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary/50" />
                        {plan.discountPct > 0 ? (
                          <span className="text-muted-foreground text-sm">
                            <span className="line-through">${(plan.priceMonthly / plan.privateCredits).toFixed(2)}</span>
                            {" "}
                            <span className="text-emerald-400 font-medium">${((plan.priceMonthly / plan.privateCredits) * (1 - plan.discountPct / 100)).toFixed(2)}</span>
                            {" per session"}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">${(plan.priceMonthly / plan.privateCredits).toFixed(2)} per session</span>
                        )}
                      </div>
                    )}
                    {plan.discountPct > 0 && (
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-emerald-400" />
                        <span className="text-emerald-400 text-sm font-medium">{plan.discountPct}% off private sessions</span>
                      </div>
                    )}
                  </div>
                  <Button onClick={() => handleSubscribe(plan.id)} disabled={checkoutMutation.isPending} className={`w-full h-12 rounded-xl font-bold ${i === 1 ? "bg-primary text-black" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"}`}>
                    {hasActive ? "Switch Plan" : "Subscribe"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
